import {
  ChangeEventHandler,
  DragEventHandler,
  FunctionComponent,
  useContext,
  useRef,
  useState
} from "react";
import LayoutContext from "../../context/layout-context";
import styles from "../file-uploader/FileUploader.module.scss";
import { resources } from "../../utils/resources";

interface MultiFileUploaderProps {
  acceptedTypes: string[];
  titleText: string;
  subtitle?: string;
  className?: string;
  onFilesChange: (files: File[]) => void;
  currentFiles: File[];
}

export const MultiFileUploader: FunctionComponent<MultiFileUploaderProps> =
  props => {
    const [dragHover, setDragHover] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const { notify } = useContext(LayoutContext);

    const {
      acceptedTypes,
      titleText,
      subtitle,
      onFilesChange,
      currentFiles,
      className
    } = props;

    const validateAndAddFiles = (newFilesList: FileList) => {
      const newFiles = Array.from(newFilesList);
      const validFiles: File[] = [];
      const invalidFileNames: string[] = [];

      newFiles.forEach(file => {
        const ext = file.name.split(".").pop()?.toLowerCase();
        let isValid = true;

        if (
          !acceptedTypes.some(acceptedType => {
            if (acceptedType.startsWith(".")) {
              return acceptedType === `.${ext}`;
            }
            return file.type.startsWith(acceptedType.split("/")[0]);
          })
        ) {
          isValid = false;
          invalidFileNames.push(file.name);
        }

        if (isValid) {
          validFiles.push(file);
        }
      });

      if (invalidFileNames.length > 0) {
        const typeErrorMsg = `Invalid file type(s): ${invalidFileNames.join(
          ", "
        )}. Valid types: ${acceptedTypes.join(", ")}`;
        notify("error", typeErrorMsg);
      }

      // Filter out duplicates and add valid files
      const uniqueFiles = validFiles.filter(
        file => !currentFiles.some(f => f.name === file.name)
      );

      const updatedFiles = [...currentFiles, ...uniqueFiles];
      onFilesChange(updatedFiles);
    };

    const onDrop: DragEventHandler = e => {
      e.stopPropagation();
      e.preventDefault();
      setDragHover(false);
      if (e.dataTransfer.files.length > 0) {
        validateAndAddFiles(e.dataTransfer.files);
      }
    };

    const handleFileUpload: ChangeEventHandler<HTMLInputElement> = e => {
      setDragHover(false);
      if (e.target.files && e.target.files.length > 0) {
        validateAndAddFiles(e.target.files);
      }
      e.target.value = "";
    };

    const handleClearFile = (fileNameToRemove: string) => {
      const updatedFiles = currentFiles.filter(
        f => f.name !== fileNameToRemove
      );
      onFilesChange(updatedFiles);
    };

    const handleClearAll = () => {
      onFilesChange([]);
    };

    const displayedContent = (
      <>
        <span className={styles.add}>{titleText || "Click or Drag Files"}</span>
        <span className={styles.subtitle}>
          {subtitle || "Drop file(s) here to upload"}
        </span>
      </>
    );

    return (
      <div className={className}>
        <label
          className={[
            styles.wrapper,
            dragHover && styles["drag-hover"],
            styles["multi-file-mode"]
          ].join(" ")}
          onDrop={onDrop}
          onDragOver={e => e.preventDefault()}
          onDragEnter={() => setDragHover(true)}
          onDragLeave={() => setDragHover(false)}
        >
          <input
            type="file"
            accept={acceptedTypes.join(",")}
            ref={inputRef}
            className={styles.input}
            onChange={handleFileUpload}
            multiple
          />
          {displayedContent}
        </label>

        {currentFiles.length > 0 && (
          <div className={styles.fileListContainer}>
            <strong className={styles.fileListTitle}>
              Selected Files ({currentFiles.length})
            </strong>
            <ul className={styles.fileList}>
              {currentFiles.map((file, index) => (
                <li key={index} className={styles.fileItem}>
                  <span className={styles.fileNameText}>{file.name}</span>
                  <img
                    onClick={e => {
                      e.stopPropagation();
                      handleClearFile(file.name);
                    }}
                    src={resources.icons.COLOR_CANCEL}
                    alt="clear"
                    className={styles.clear}
                    title="Remove file"
                  />
                </li>
              ))}
            </ul>
            <button
              type="button"
              onClick={handleClearAll}
              className={styles.clearAllButton}
            >
              Clear All
            </button>
          </div>
        )}
      </div>
    );
  };
